import { Button } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type OutgoingInvoiceOfInvoiceStatsFragment,
  type ProjectInvoiceStat,
  useInvoiceProjectMutation,
} from "@src/__generated__/graphql";
import { TableBody } from "@src/components/modules/common/table";
import { InvoiceNoColumn } from "@src/components/modules/invoices/outgoing/components/columns";
import { Card, ColoredAmount } from "@src/components/ui-kit";
import { type TColumnDef, useTable } from "@src/components/ui-kit/table";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { currency, dateTime } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import type React from "react";
import type { FC } from "react";

type TInvoiceStats = Pick<ProjectInvoiceStat, "id" | "credit"> & {
  outgoingInvoice: OutgoingInvoiceOfInvoiceStatsFragment;
};

interface TableInvoicesProps {
  data: TInvoiceStats[];
  invoicedAmount: number;
  toInvoiceAmount?: number;

  /** Invoice action is provided when projectId is present */
  projectId?: string;

  ourWorkBudgetItemId?: string;
  expenseBudgetIdemId?: string;

  isProjectBillable?: boolean;
}

const TableInvoices: FC<React.PropsWithChildren<TableInvoicesProps>> = ({
  data,
  invoicedAmount,
  toInvoiceAmount,
  projectId,
  ourWorkBudgetItemId,
  expenseBudgetIdemId,
  isProjectBillable,
}) => {
  const router = useRouter();

  const { table } = useTable(data, cols);

  const [invoiceProject, { loading }] = useInvoiceProjectMutation({
    onCompleted(data) {
      if (data?.invoiceProject?.id) {
        router.push({
          pathname: "/invoices/for-invoicing/edit",
          query: { id: data.invoiceProject?.id },
        });
      }
    },
    ...onError(),
  });

  const onInvoiceProject = () => {
    invoiceProject({
      variables: {
        id: projectId!,
        amount: toInvoiceAmount,
        our_work_budget_item_id: ourWorkBudgetItemId,
        expense_budget_item_id: expenseBudgetIdemId,
      },
    });
  };

  return (
    <div className="px-8 py-12">
      <div className="flex items-center h-full mb-10">
        <span className="mr-2">
          <Trans>Invoiced</Trans>:
        </span>
        <span className="font-bold text-2xl">
          {currency.formatByWorkspace(invoicedAmount)}
        </span>
      </div>

      <Card>
        <div className="pb-10">
          <div className="h-24 flex items-center justify-between px-6">
            <h2 className="text-2xl font-bold">
              <Trans>Invoices</Trans>
            </h2>
            <div className="flex">
              {can("credit_transfer_fromBudgetItem") &&
                !!projectId &&
                isProjectBillable && (
                  <Button isLoading={loading} onClick={onInvoiceProject}>
                    <Trans>Invoice</Trans>
                  </Button>
                )}
            </div>
          </div>

          <TableBody
            table={table}
            customTBody={
              data.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="flex items-center justify-center h-32 w-full">
                      {t`No invoices to display`}
                    </div>
                  </td>
                </tr>
              ) : undefined
            }
          />
        </div>
      </Card>
    </div>
  );
};

const cols: TColumnDef<TInvoiceStats, any> = [
  {
    key: "date_of_supply",
    header: () => t`Date`,
    render: (r) =>
      r.outgoingInvoice.date_of_supply
        ? dateTime.format(new Date(r.outgoingInvoice.date_of_supply))
        : "",
  },
  {
    key: "invoice_no",
    header: () => t`Invoice no`,
    render: (r) => (
      <InvoiceNoColumn
        invoice={{
          ...r.outgoingInvoice,
          abraflexi_exported: r.outgoingInvoice.abraflexi_exported,
          xero_exported: r.outgoingInvoice.xero_exported,
          proformaInvoicesPivot: r.outgoingInvoice.proformaInvoicesPivot.map(
            ({ invoice }) => invoice,
          ),
        }}
      />
    ),
  },
  {
    key: "amount",
    header: () => t`Amount`,
    render: (r) => {
      return (
        <ColoredAmount value={r.credit}>
          {currency.formatByWorkspace(r.credit)}
        </ColoredAmount>
      );
    },
  },
  {
    key: "billing_amount",
    header: () => t`Billing amount`,
    render: (r) =>
      currency.formatByWorkspace(r.outgoingInvoice.stats.billing_amount),
  },
];

export default observer(TableInvoices);
